import { useTranslation } from 'react-i18next';

import { useEffect, useState } from 'react';

import { useForm } from '@mantine/form';

import { IconAlertHexagon, IconArrowLeft, IconMail, IconNotification } from '@tabler/icons-react';

import { Link, createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';

import {
  Anchor,
  Button,
  Container,
  Group,
  Notification,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
  Transition,
} from '@optiro/ui';

import { InteractiveLoader } from '@/components/InteractiveLoader';
import { useCheckEmail } from '@/hooks/useCheckEmail';
import { useAuth } from '@/providers/AuthProvider/AuthProvider';
import { useExperiences } from '@/providers/ExperiencesProvider';

type LoginSearchParams = {
  error?: string;
  notice?: string;
};

export const Route = createFileRoute('/experiences/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearchParams => {
    return {
      error: search.error as string,
      notice: search.notice as string,
    };
  },
  component: Login,
});

type AuthMode = 'initial' | 'login';

function Login() {
  const [mode, setMode] = useState<AuthMode>('initial');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isSignedIn } = useAuth();
  const { returnUrl } = useExperiences();
  const checkEmail = useCheckEmail();
  const navigate = useNavigate({ from: '/experiences/login' });
  const searchParams = useSearch({ from: '/experiences/login' });
  const { t: errorT } = useTranslation('errors');
  const { t } = useTranslation('errors');

  const form = useForm({
    initialValues: {
      email: '',
      password: '',
    },
    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Ugyldig email'),
      password: (value) => (mode === 'login' && !value ? 'Kodeord er påkrævet' : null),
    },
  });

  // Set returnUrl from query params when component mounts

  // Redirect if already signed in
  useEffect(() => {
    if (isSignedIn) {
      if (returnUrl) {
        window.location.href = returnUrl;
      } else {
        navigate({ to: '/app/dashboard' });
      }
    }
  }, [isSignedIn, returnUrl, navigate]);

  const handleEmailSubmit = (values: typeof form.values) => {
    setError('');

    checkEmail.mutate(values.email, {
      onSuccess: (data) => {
        if (data.exists) {
          setMode('login');
        } else {
          navigate({
            to: '/experiences/signup',
            search: {
              email: values.email,
              registrationContext: undefined,
              returnUrl: returnUrl ?? undefined,
            },
          });
        }
      },
      onError: () => {
        setError('Der opstod en fejl. Prøv venligst igen.');
      },
    });
  };

  const handleLogin = (values: typeof form.values) => {
    setError('');
    setLoading(true);

    login(values.email, values.password)
      .catch((err) => {
        setError(t(err.message));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleBack = () => {
    setMode('initial');
    form.setFieldValue('password', '');
    setError('');
  };

  const isLoginMode = mode === 'login';
  const showAuthFields = isLoginMode;

  const handleSubmit = form.onSubmit((values) => {
    if (mode === 'initial') {
      handleEmailSubmit(values);
    } else if (isLoginMode) {
      handleLogin(values);
    }
  });

  if (isSignedIn) return null;

  return (
    <Container size="xs" py="xl">
      <Paper p="xl" radius="md" withBorder shadow="sm" bg="white">
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            {/* Error/Notice notifications */}
            {mode === 'initial' && searchParams.error && (
              <Notification
                withBorder
                icon={<IconAlertHexagon stroke={1.2} />}
                withCloseButton={false}
                color="red"
                title="Advarsel!"
              >
                {errorT(searchParams.error)}
              </Notification>
            )}
            {mode === 'initial' && searchParams.notice && (
              <Notification
                withBorder
                withCloseButton={false}
                color="yellow"
                icon={<IconNotification stroke={1.2} />}
                title={errorT(searchParams.notice)}
              />
            )}

            {/* Back button for login */}
            {showAuthFields && (
              <Group gap="xs">
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={handleBack}
                  leftSection={<IconArrowLeft size={16} />}
                  px={0}
                >
                  Tilbage
                </Button>
              </Group>
            )}

            {/* Title */}
            <Title order={2} ta="center" fw={500}>
              {mode === 'initial' && 'Velkommen til Optiro'}
              {isLoginMode && 'Velkommen tilbage'}
            </Title>

            {mode === 'initial' && (
              <Text size="sm" c="dimmed" ta="center">
                Indtast din email for at fortsætte
              </Text>
            )}

            {isLoginMode && (
              <Text size="sm" c="dimmed" ta="center">
                Indtast dit kodeord for at logge ind
              </Text>
            )}

            {/* Loading state for login only */}
            {isLoginMode && loading ? (
              <InteractiveLoader
                message="Logger ind"
                rotatingMessages={[
                  'Bekræfter dine oplysninger...',
                  'Indlæser din profil...',
                  'Næsten færdig...',
                ]}
              />
            ) : (
              <>
                {/* Email field - always shown */}
                <TextInput
                  id="email"
                  name="email"
                  size="md"
                  required
                  leftSection={<IconMail size={16} />}
                  label="Email"
                  placeholder="navn@din-virksomhed.dk"
                  type="email"
                  autoComplete="username"
                  disabled={showAuthFields}
                  {...form.getInputProps('email')}
                  rightSection={
                    checkEmail.isPending ? (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          style={{ animation: 'spin 1s linear infinite' }}
                        >
                          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                        </svg>
                      </div>
                    ) : undefined
                  }
                />

                {/* Password field - shown for login */}
                <Transition
                  mounted={showAuthFields}
                  transition="slide-down"
                  duration={300}
                  timingFunction="ease"
                  keepMounted
                >
                  {(styles) => (
                    <div style={styles}>
                      <PasswordInput
                        label="Kodeord"
                        name="password"
                        size="md"
                        required={showAuthFields}
                        autoComplete={isLoginMode ? 'current-password' : 'new-password'}
                        description={
                          isLoginMode ? (
                            <Anchor
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                navigate({
                                  to: '/experiences/forgot-password',
                                  search: { returnUrl: undefined },
                                });
                              }}
                              pt={2}
                              fw={500}
                              fz="xs"
                            >
                              Glemt din kode?
                            </Anchor>
                          ) : undefined
                        }
                        placeholder="Din kode"
                        {...form.getInputProps('password')}
                        error={form.errors.password}
                      />
                    </div>
                  )}
                </Transition>
                {/* Error message */}
                {error && (
                  <Text c="red" size="sm">
                    {error}
                  </Text>
                )}

                {/* Submit button */}
                <Button
                  variant="filled"
                  size="lg"
                  type="submit"
                  fullWidth={showAuthFields}
                  style={{ backgroundColor: '#3c735b' }}
                >
                  {mode === 'initial' && 'Fortsæt'}
                  {isLoginMode && 'Login'}
                </Button>

                {/* Privacy policy link for initial mode */}
                {mode === 'initial' && (
                  <Link
                    target="_blank"
                    to={import.meta.env.VITE_LANDING_PAGE_URL + '/privacy-policy'}
                  >
                    <Anchor size="xs" c="dimmed" ta="center" display="block">
                      Privatlivspolitik
                    </Anchor>
                  </Link>
                )}

                {/* Privacy policy link for login */}
                {isLoginMode && (
                  <Link
                    target="_blank"
                    to={import.meta.env.VITE_LANDING_PAGE_URL + '/privacy-policy'}
                  >
                    <Anchor size="xs" c="dimmed" ta="center" display="block">
                      Privatlivspolitik
                    </Anchor>
                  </Link>
                )}
              </>
            )}
          </Stack>
        </form>
      </Paper>
    </Container>
  );
}
